import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { marketingObjectiveEntity, marketingExperimentEntity, marketingSprintEntity } from 'mj_generatedentities';

interface RoadmapRow {
    code: string;
    name: string;
    cells: ('active' | 'planned' | 'past' | null)[];
}

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-objectives',
    template: `
<mkt-ws-page-head
    eyebrow="Strategy · Objectives & Plan"
    title="Objectives & Plan"
    sub="Manually entered objectives plus the experiment roadmap behind them. Each objective tracks KR progress; experiments roll up across sprints."
    [hasActions]="true">
</mkt-ws-page-head>

<div *ngIf="loading" class="ob__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading objectives…</div>

<ng-container *ngIf="!loading">
    <div class="ob__layout">
        <div class="ob__main">
            <mkt-ws-card [hasHead]="true" [noPadding]="true">
                <div head>
                    <span class="ob__caps-ink">Objectives · {{ objectives.length }}</span>
                    <span class="ob__sub" *ngIf="lastEdited">Last updated {{ formatDate(lastEdited) }}</span>
                </div>
                <div *ngIf="objectives.length === 0" class="ob__empty">
                    No objectives yet. Use MJ Explorer's Objectives entity to add 1–3 quarterly goals — they roll up across experiments and content programs.
                </div>
                <div *ngFor="let o of objectives; let i = index" class="ob__row">
                    <div class="ob__row-head">
                        <span class="ob__caps">Objective {{ pad(i + 1) }}</span>
                        <mkt-ws-pill [variant]="statusVariant(o.Status)" [dot]="true">{{ statusLabel(o.Status) }}</mkt-ws-pill>
                    </div>
                    <div class="ob__row-title">{{ o.Title }}</div>
                    <div class="ob__row-body" *ngIf="o.Description">{{ o.Description }}</div>
                    <div class="ob__progress" *ngIf="o.TargetValue != null">
                        <div class="ob__progress-row">
                            <span class="ob__progress-label">{{ o.TargetUnit || 'Progress' }}</span>
                            <div class="ob__bar-track"><div class="ob__bar-fill" [style.width.%]="progressPct(o)"></div></div>
                            <span class="ob__mono ob__progress-num">{{ formatNum(o.CurrentValue) }} / {{ formatNum(o.TargetValue) }}</span>
                        </div>
                    </div>
                    <div class="ob__experiments" *ngIf="experimentsByObjective[o.ID]?.length">
                        <span class="ob__caps">Experiments testing this</span>
                        <div class="ob__exp-list">
                            <button *ngFor="let e of experimentsByObjective[o.ID]" type="button" class="ob__exp-tag" (click)="openExperiment(e.ID)">
                                <span class="ob__mono">{{ e.Code || e.ID.slice(0,8) }}</span>
                                <span class="ob__exp-tag-name">{{ e.Name || e.Hypothesis }}</span>
                                <mkt-ws-pill variant="muted">{{ e.ExperimentStatus }}</mkt-ws-pill>
                            </button>
                        </div>
                    </div>
                </div>
            </mkt-ws-card>

            <mkt-ws-card *ngIf="roadmap.length > 0" [hasHead]="true">
                <div head>
                    <span class="ob__caps-ink">Experiment roadmap</span>
                    <span class="ob__sub">{{ sprintHeaders.length > 0 ? 'Sprints ' + sprintHeaders[0] + ' → ' + sprintHeaders[sprintHeaders.length - 1] : 'No sprints' }}</span>
                </div>
                <div class="ob__roadmap">
                    <div class="ob__roadmap-headers">
                        <div></div>
                        <div *ngFor="let h of sprintHeaders" class="ob__caps" style="text-align:center;">Sp {{ h }}</div>
                    </div>
                    <div *ngFor="let r of roadmap" class="ob__roadmap-row">
                        <button type="button" class="ob__roadmap-name" (click)="openExperiment(r.expId)" [title]="r.name">
                            <span class="ob__mono">{{ r.code }}</span> {{ r.name }}
                        </button>
                        <div class="ob__roadmap-cells">
                            <div *ngFor="let cell of r.cells" class="ob__roadmap-cell" [attr.data-state]="cell || ''"></div>
                        </div>
                    </div>
                </div>
            </mkt-ws-card>
        </div>
    </div>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .ob__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }

        .ob__caps, .ob__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .ob__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .ob__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .ob__mono { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .ob__layout { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .ob__main { display: flex; flex-direction: column; gap: 16px; }

        .ob__row { padding: 18px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .ob__row:last-child { border-bottom: none; }
        .ob__row-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .ob__row-title { font-size: 15px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); margin-bottom: 8px; line-height: 1.4; }
        .ob__row-body { font-size: 13px; color: var(--mj-text-secondary, #71717a); margin-bottom: 12px; line-height: 1.6; }

        .ob__progress { margin-bottom: 12px; }
        .ob__progress-row { display: flex; align-items: center; gap: 12px; font-size: 12.5px; }
        .ob__progress-label { color: var(--mj-text-secondary, #71717a); width: 140px; }
        .ob__bar-track { flex: 1; height: 6px; background: var(--mj-bg-page, #f5f5f5); border-radius: 3px; overflow: hidden; }
        .ob__bar-fill { height: 100%; background: var(--mj-text-primary, #1a1a1a); border-radius: 3px; }
        .ob__progress-num { width: 110px; text-align: right; }

        .ob__experiments { padding-top: 8px; border-top: 1px dashed var(--mj-border-default, #e0e0e0); }
        .ob__exp-list { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
        .ob__exp-tag { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); cursor: pointer; font: inherit; text-align: left; transition: background 0.15s; }
        .ob__exp-tag:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ob__exp-tag-name { flex: 1; font-size: 12.5px; color: var(--mj-text-primary, #1a1a1a); }

        .ob__empty { padding: 32px; text-align: center; font-size: 12.5px; color: var(--mj-text-secondary, #71717a); }

        .ob__roadmap { display: flex; flex-direction: column; gap: 6px; font-size: 12.5px; }
        .ob__roadmap-headers { display: grid; grid-template-columns: 220px 1fr; gap: 6px; align-items: center; }
        .ob__roadmap-headers > :nth-child(n+2) { display: grid; grid-template-columns: repeat(var(--sprints, 6), 1fr); gap: 4px; }
        .ob__roadmap-row { display: grid; grid-template-columns: 220px 1fr; gap: 6px; align-items: center; }
        .ob__roadmap-name { background: none; border: none; padding: 0; text-align: left; font: inherit; font-size: 12.5px; color: var(--mj-text-primary, #1a1a1a); cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .ob__roadmap-name:hover { text-decoration: underline; }
        .ob__roadmap-cells { display: grid; grid-template-columns: repeat(var(--sprints, 6), 1fr); gap: 4px; height: 18px; }
        .ob__roadmap-cell { border-radius: 2px; }
        .ob__roadmap-cell[data-state="active"] { background: var(--mj-text-primary, #1a1a1a); }
        .ob__roadmap-cell[data-state="planned"] { background: var(--mj-text-primary, #1a1a1a); opacity: 0.4; }
        .ob__roadmap-cell[data-state="past"] { background: var(--mj-text-primary, #1a1a1a); opacity: 0.2; }
    `]
})
/**
 * Per-section affordance gating note (#938): currently read-only — objectives
 * are entered via MJ Explorer's Objectives entity, not in-section. Route is
 * already `requiredRole: 'Admin'`. When inline objective-edit affordances are
 * added, gate them on `userRole === null || ROLE_RANK[userRole] >= ROLE_RANK['Admin']`.
 */
export class StrategyObjectivesSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
            this.objectives = [];
            this.cdr.detectChanges();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    objectives: marketingObjectiveEntity[] = [];
    experimentsByObjective: Record<string, marketingExperimentEntity[]> = {};
    roadmap: (RoadmapRow & { expId: string })[] = [];
    sprintHeaders: number[] = [];
    lastEdited: Date | null = null;

    pad(n: number): string {
        return n.toString().padStart(2, '0');
    }
    formatDate(d: Date | string | null | undefined): string {
        if (!d) return '—';
        return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    formatNum(n: number | null | undefined): string {
        if (n == null) return '—';
        return Number.isInteger(n) ? String(n) : n.toFixed(1);
    }
    statusLabel(s: string): string {
        switch (s) {
            case 'OnTrack': return 'On track';
            case 'AtRisk': return 'At risk';
            case 'Achieved': return 'Achieved';
            case 'Missed': return 'Missed';
            case 'Blocked': return 'Blocked';
            default: return s || '—';
        }
    }
    statusVariant(s: string): 'good' | 'warn' | 'bad' | 'muted' | 'info' {
        switch (s) {
            case 'OnTrack':
            case 'Achieved': return 'good';
            case 'AtRisk': return 'warn';
            case 'Blocked':
            case 'Missed': return 'bad';
            default: return 'muted';
        }
    }
    progressPct(o: marketingObjectiveEntity): number {
        if (o.TargetValue == null || o.TargetValue === 0) return 0;
        const p = ((o.CurrentValue ?? 0) / o.TargetValue) * 100;
        return Math.max(0, Math.min(100, p));
    }
    openExperiment(id: string): void {
        this.navigateWithId('experiments/detail', id);
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [objRes, expRes, sprintRes] = await rv.RunViews([
                {
                    EntityName: 'Objectives',
                    ExtraFilter: `CompanyID='${cid}'`,
                    OrderBy: 'IsActive DESC, Sequence ASC, __mj_CreatedAt DESC',
                    MaxRows: 50,
                    ResultType: 'entity_object',
                },
                {
                    EntityName: 'Experiments',
                    ExtraFilter: `CompanyID='${cid}' AND ObjectiveID IS NOT NULL`,
                    OrderBy: 'StartDate ASC',
                    MaxRows: 200,
                    ResultType: 'simple',
                },
                {
                    EntityName: 'Sprints',
                    ExtraFilter: `CompanyID='${cid}'`,
                    OrderBy: 'StartDate ASC',
                    MaxRows: 12,
                    ResultType: 'simple',
                },
            ]);
            if (objRes.Success) {
                this.objectives = (objRes.Results || []) as marketingObjectiveEntity[];
                const updated = this.objectives.map(o => o.__mj_UpdatedAt).filter(d => !!d);
                if (updated.length > 0) {
                    this.lastEdited = new Date(Math.max(...updated.map(d => new Date(d).getTime())));
                }
            }
            const experiments = expRes.Success ? (expRes.Results || []) as marketingExperimentEntity[] : [];
            const sprints = sprintRes.Success ? (sprintRes.Results || []) as marketingSprintEntity[] : [];
            this.experimentsByObjective = {};
            for (const e of experiments) {
                if (!e.ObjectiveID) continue;
                (this.experimentsByObjective[e.ObjectiveID] ||= []).push(e);
            }
            this.buildRoadmap(experiments, sprints);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyObjectives] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private buildRoadmap(experiments: marketingExperimentEntity[], sprints: marketingSprintEntity[]): void {
        if (sprints.length === 0 || experiments.length === 0) {
            this.roadmap = [];
            this.sprintHeaders = [];
            return;
        }
        // Pick a 6-sprint window centered on current sprint
        const now = Date.now();
        const sortedSprints = [...sprints].sort((a, b) => new Date(a.StartDate).getTime() - new Date(b.StartDate).getTime());
        const currentIdx = sortedSprints.findIndex(s => new Date(s.EndDate).getTime() >= now);
        const startIdx = Math.max(0, (currentIdx >= 0 ? currentIdx : sortedSprints.length - 1) - 1);
        const window = sortedSprints.slice(startIdx, startIdx + 6);
        if (window.length === 0) return;

        this.sprintHeaders = window.map(s => s.SprintNumber || 0);

        const rows: (RoadmapRow & { expId: string })[] = experiments
            .filter(e => e.StartDate || e.DecisionDate)
            .slice(0, 12)
            .map(e => {
                const start = e.StartDate ? new Date(e.StartDate).getTime() : null;
                const end = e.DecisionDate ? new Date(e.DecisionDate).getTime() : null;
                const cells: ('active' | 'planned' | 'past' | null)[] = window.map(s => {
                    const sStart = new Date(s.StartDate).getTime();
                    const sEnd = new Date(s.EndDate).getTime();
                    if (start == null || end == null) return null;
                    if (sEnd < start || sStart > end) return null;
                    if (sEnd < now) return 'past';
                    if (sStart > now) return 'planned';
                    return 'active';
                });
                return {
                    expId: e.ID,
                    code: e.Code || e.ID.slice(0, 8),
                    name: e.Name || e.Hypothesis || 'Experiment',
                    cells,
                };
            })
            .filter(r => r.cells.some(c => c !== null));

        this.roadmap = rows;
    }
}
